import React, { useState } from 'react';
import { Star, X, Send, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import ReportTrainerModal from './ReportTrainerModal';

const ReviewModal = ({ isOpen, onClose, booking, onSubmitSuccess }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: 'Error',
        description: 'Please select a rating',
        variant: 'destructive'
      });
      return;
    }

    if (!comment.trim()) {
      toast({
        title: 'Error',
        description: 'Please write a comment',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trainer_id: booking.trainer_id,
          booking_id: booking.id,
          rating: rating,
          comment: comment
        })
      });

      if (response.ok) {
        toast({
          title: 'Review Submitted!',
          description: 'Your review has been submitted and is pending admin approval.'
        });
        setRating(0);
        setComment('');
        if (onSubmitSuccess) onSubmitSuccess();
        onClose();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to submit review',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit review',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Rate Your Training Session
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Trainer Info */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <p className="text-gray-600">Training with</p>
              <Button
                onClick={() => setShowReportModal(true)}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                title="Report this trainer"
              >
                <AlertTriangle className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xl font-semibold text-red-600">{booking.trainer_name}</p>
            <p className="text-xs text-gray-500 mt-1">
              Having issues? Click the ⚠️ icon to report
            </p>
          </div>

          {/* Star Rating */}
          <div className="flex flex-col items-center gap-3">
            <p className="font-medium text-gray-700">How would you rate this session?</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                >
                  <Star
                    className={`w-10 h-10 ${
                      star <= (hover || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-gray-600">
                {rating === 1 && '⭐ Poor'}
                {rating === 2 && '⭐⭐ Fair'}
                {rating === 3 && '⭐⭐⭐ Good'}
                {rating === 4 && '⭐⭐⭐⭐ Very Good'}
                {rating === 5 && '⭐⭐⭐⭐⭐ Excellent'}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share your experience
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us about your training session..."
              rows={4}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Your review will be visible after admin approval
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-2">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              className="flex-1 bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
            >
              {submitting ? (
                <>Submitting...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Review
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Report Trainer Modal */}
      <ReportTrainerModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        trainer={{
          id: booking?.trainer_id || booking?.user_id,  // Use trainer_id from booking
          name: booking?.trainer_name
        }}
        onReportSuccess={() => {
          toast({
            title: 'Report Submitted',
            description: 'Your report has been submitted to admin for review.'
          });
        }}
      />
    </Dialog>
  );
};

export default ReviewModal;
